// Bridges @lely/tokens SCSS into JS for MUI createTheme (:export).
// Values come from node_modules — update tokens by bumping @lely/tokens.

@use 'sass:map';
@use '@lely/tokens/dist/colors.scss' as colors;
@use '@lely/tokens/dist/spacing.scss' as spacing;
@use '@lely/tokens/dist/typography/text-base.scss' as textBase;
@use '@lely/tokens/dist/typography/text-sizes.scss' as textSizes;
@use '@lely/tokens/dist/border-properties.scss' as borderProps;
@use '@lely/tokens/dist/borders.scss' as borders;
@use '@lely/tokens/dist/box-shadow.scss' as boxShadow;
@font-face {
  font-family: 'Noto Sans';
  src: local('Noto Sans'),
    url('/fonts/noto sans/NotoSans-Regular.ttf') format('truetype');
}
$_text: textSizes.$body-text-tokens;

:export {
  // Font families & weights
  fontFamilyUbuntu: textBase.$font-family-ubuntu;
  fontFamilyNoto: textBase.$font-family-noto;
  fontWeightBold: textBase.$font-weight-bold;
  fontWeightMedium: textBase.$font-weight-medium;
  fontWeightRegular: textBase.$font-weight-regular;

  // Spacing scale (matches $spacing map — 4px grid friendly for MUI spacing factor)
  space2xs: map.get(spacing.$spacing, 2xs);
  spaceXs: map.get(spacing.$spacing, xs);
  spaceSm: map.get(spacing.$spacing, sm);
  spaceMd: map.get(spacing.$spacing, md);
  spaceLg: map.get(spacing.$spacing, lg);
  spaceXl: map.get(spacing.$spacing, xl);
  space2xl: map.get(spacing.$spacing, 2xl);
  space3xl: map.get(spacing.$spacing, 3xl);
  space4xl: map.get(spacing.$spacing, 4xl);
  space5xl: map.get(spacing.$spacing, 5xl);
  space6xl: map.get(spacing.$spacing, 6xl);
  space7xl: map.get(spacing.$spacing, 7xl);
  space8xl: map.get(spacing.$spacing, 8xl);
  space9xl: map.get(spacing.$spacing, 9xl);
  space10xl: map.get(spacing.$spacing, 10xl);
  space11xl: map.get(spacing.$spacing, 11xl);
  space12xl: map.get(spacing.$spacing, 12xl);
  space13xl: map.get(spacing.$spacing, 13xl);
  space14xl: map.get(spacing.$spacing, 14xl);
  space15xl: map.get(spacing.$spacing, 15xl);
  space16xl: map.get(spacing.$spacing, 16xl);

  // Body / display scale (font-size & line-height)
  textXsFontSize: map.get(map.get($_text, xs), 'font-size');
  textXsLineHeight: map.get(map.get($_text, xs), 'line-height');
  textMdFontSize: map.get(map.get($_text, md), 'font-size');
  textMdLineHeight: map.get(map.get($_text, md), 'line-height');
  textBaseFontSize: map.get(map.get($_text, base), 'font-size');
  textBaseLineHeight: map.get(map.get($_text, base), 'line-height');
  textLgFontSize: map.get(map.get($_text, lg), 'font-size');
  textLgLineHeight: map.get(map.get($_text, lg), 'line-height');
  textXlFontSize: map.get(map.get($_text, xl), 'font-size');
  textXlLineHeight: map.get(map.get($_text, xl), 'line-height');
  text2xlFontSize: map.get(map.get($_text, 2xl), 'font-size');
  text2xlLineHeight: map.get(map.get($_text, 2xl), 'line-height');
  text3xlFontSize: map.get(map.get($_text, 3xl), 'font-size');
  text3xlLineHeight: map.get(map.get($_text, 3xl), 'line-height');
  text4xlFontSize: map.get(map.get($_text, 4xl), 'font-size');
  text4xlLineHeight: map.get(map.get($_text, 4xl), 'line-height');
  text5xlFontSize: map.get(map.get($_text, 5xl), 'font-size');
  text5xlLineHeight: map.get(map.get($_text, 5xl), 'line-height');
  text6xlFontSize: map.get(map.get($_text, 6xl), 'font-size');
  text6xlLineHeight: map.get(map.get($_text, 6xl), 'line-height');

  // Border width (@lely/tokens/dist/border-properties.scss)
  borderWidthXl: borderProps.$border-width-xl;
  borderWidthLg: borderProps.$border-width-lg;
  borderWidthBase: borderProps.$border-width-base;

  // Border radius
  borderRadiusSm: borderProps.$border-radius-sm;
  borderRadiusMd: borderProps.$border-radius-md;
  borderRadiusLg: borderProps.$border-radius-lg;
  borderRadiusXl: borderProps.$border-radius-xl;
  borderRadius2xl: borderProps.$border-radius-2xl;
  borderRadius3xl: borderProps.$border-radius-3xl;
  borderRadius4xl: borderProps.$border-radius-4xl;

  // Preset border styles (width + style + colour — @lely/tokens/dist/borders.scss)
  borderNormal: borders.$border-normal;
  borderDashed: borders.$border-dashed;
  borderFocus: borders.$border-focus;

  // Elevation (subset mapped onto MUI paper elevations 1–4)
  boxShadowSm: boxShadow.$box-shadow-sm;
  boxShadowMd: boxShadow.$box-shadow-md;
  boxShadowLg: boxShadow.$box-shadow-lg;
  boxShadowXl: boxShadow.$box-shadow-xl;
  boxShadow2xl: boxShadow.$box-shadow-2xl;
  boxShadow3xl: boxShadow.$box-shadow-3xl;

  // Palette primitives — full @lely/tokens/dist/colors.scss scale (semantic mapping in LelyMuiTheme.ts)
  colorWhite100: colors.$color-white-100;
  colorBlack100: colors.$color-black-100;

  colorNeutral10: colors.$color-neutral-10;
  colorNeutral20: colors.$color-neutral-20;
  colorNeutral30: colors.$color-neutral-30;
  colorNeutral40: colors.$color-neutral-40;
  colorNeutral50: colors.$color-neutral-50;
  colorNeutral60: colors.$color-neutral-60;
  colorNeutral70: colors.$color-neutral-70;
  colorNeutral80: colors.$color-neutral-80;
  colorNeutral90: colors.$color-neutral-90;
  colorNeutral99: colors.$color-neutral-99;

  colorMagenta10: colors.$color-magenta-10;
  colorMagenta20: colors.$color-magenta-20;
  colorMagenta30: colors.$color-magenta-30;
  colorMagenta40: colors.$color-magenta-40;
  colorMagenta50: colors.$color-magenta-50;
  colorMagenta60: colors.$color-magenta-60;
  colorMagenta70: colors.$color-magenta-70;
  colorMagenta80: colors.$color-magenta-80;
  colorMagenta90: colors.$color-magenta-90;
  colorMagenta99: colors.$color-magenta-99;

  colorPurple10: colors.$color-purple-10;
  colorPurple20: colors.$color-purple-20;
  colorPurple30: colors.$color-purple-30;
  colorPurple40: colors.$color-purple-40;
  colorPurple50: colors.$color-purple-50;
  colorPurple60: colors.$color-purple-60;
  colorPurple70: colors.$color-purple-70;
  colorPurple80: colors.$color-purple-80;
  colorPurple90: colors.$color-purple-90;
  colorPurple99: colors.$color-purple-99;

  colorCyan10: colors.$color-cyan-10;
  colorCyan20: colors.$color-cyan-20;
  colorCyan30: colors.$color-cyan-30;
  colorCyan40: colors.$color-cyan-40;
  colorCyan50: colors.$color-cyan-50;
  colorCyan60: colors.$color-cyan-60;
  colorCyan70: colors.$color-cyan-70;
  colorCyan80: colors.$color-cyan-80;
  colorCyan90: colors.$color-cyan-90;
  colorCyan99: colors.$color-cyan-99;

  colorTeal10: colors.$color-teal-10;
  colorTeal20: colors.$color-teal-20;
  colorTeal30: colors.$color-teal-30;
  colorTeal40: colors.$color-teal-40;
  colorTeal50: colors.$color-teal-50;
  colorTeal60: colors.$color-teal-60;
  colorTeal70: colors.$color-teal-70;
  colorTeal80: colors.$color-teal-80;
  colorTeal90: colors.$color-teal-90;
  colorTeal99: colors.$color-teal-99;

  colorGreen10: colors.$color-green-10;
  colorGreen20: colors.$color-green-20;
  colorGreen30: colors.$color-green-30;
  colorGreen40: colors.$color-green-40;
  colorGreen50: colors.$color-green-50;
  colorGreen60: colors.$color-green-60;
  colorGreen70: colors.$color-green-70;
  colorGreen80: colors.$color-green-80;
  colorGreen90: colors.$color-green-90;
  colorGreen99: colors.$color-green-99;

  colorYellow10: colors.$color-yellow-10;
  colorYellow20: colors.$color-yellow-20;
  colorYellow30: colors.$color-yellow-30;
  colorYellow40: colors.$color-yellow-40;
  colorYellow50: colors.$color-yellow-50;
  colorYellow60: colors.$color-yellow-60;
  colorYellow70: colors.$color-yellow-70;
  colorYellow80: colors.$color-yellow-80;
  colorYellow90: colors.$color-yellow-90;
  colorYellow99: colors.$color-yellow-99;

  colorOrange10: colors.$color-orange-10;
  colorOrange20: colors.$color-orange-20;
  colorOrange30: colors.$color-orange-30;
  colorOrange40: colors.$color-orange-40;
  colorOrange50: colors.$color-orange-50;
  colorOrange60: colors.$color-orange-60;
  colorOrange70: colors.$color-orange-70;
  colorOrange80: colors.$color-orange-80;
  colorOrange90: colors.$color-orange-90;
  colorOrange99: colors.$color-orange-99;

  colorRed10: colors.$color-red-10;
  colorRed20: colors.$color-red-20;
  colorRed30: colors.$color-red-30;
  colorRed40: colors.$color-red-40;
  colorRed50: colors.$color-red-50;
  colorRed60: colors.$color-red-60;
  colorRed70: colors.$color-red-70;
  colorRed80: colors.$color-red-80;
  colorRed90: colors.$color-red-90;
  colorRed99: colors.$color-red-99;

  colorError10: colors.$color-error-10;
  colorError20: colors.$color-error-20;
  colorError30: colors.$color-error-30;
  colorError40: colors.$color-error-40;
  colorError50: colors.$color-error-50;
  colorError60: colors.$color-error-60;
  colorError70: colors.$color-error-70;
  colorError80: colors.$color-error-80;
  colorError90: colors.$color-error-90;
  colorError99: colors.$color-error-99;

  colorWarning10: colors.$color-warning-10;
  colorWarning20: colors.$color-warning-20;
  colorWarning30: colors.$color-warning-30;
  colorWarning40: colors.$color-warning-40;
  colorWarning50: colors.$color-warning-50;
  colorWarning60: colors.$color-warning-60;
  colorWarning70: colors.$color-warning-70;
  colorWarning80: colors.$color-warning-80;
  colorWarning90: colors.$color-warning-90;
  colorWarning99: colors.$color-warning-99;

  colorAdvice10: colors.$color-advice-10;
  colorAdvice20: colors.$color-advice-20;
  colorAdvice30: colors.$color-advice-30;
  colorAdvice40: colors.$color-advice-40;
  colorAdvice50: colors.$color-advice-50;
  colorAdvice60: colors.$color-advice-60;
  colorAdvice70: colors.$color-advice-70;
  colorAdvice80: colors.$color-advice-80;
  colorAdvice90: colors.$color-advice-90;
  colorAdvice99: colors.$color-advice-99;

  colorSuccess10: colors.$color-success-10;
  colorSuccess20: colors.$color-success-20;
  colorSuccess30: colors.$color-success-30;
  colorSuccess40: colors.$color-success-40;
  colorSuccess50: colors.$color-success-50;
  colorSuccess60: colors.$color-success-60;
  colorSuccess70: colors.$color-success-70;
  colorSuccess80: colors.$color-success-80;
  colorSuccess90: colors.$color-success-90;
  colorSuccess99: colors.$color-success-99;

  colorA11yRed10: colors.$color-a11y-red-10;
  colorA11yRed20: colors.$color-a11y-red-20;
  colorA11yRed30: colors.$color-a11y-red-30;
  colorA11yRed40: colors.$color-a11y-red-40;
  colorA11yRed50: colors.$color-a11y-red-50;
  colorA11yRed60: colors.$color-a11y-red-60;
  colorA11yRed70: colors.$color-a11y-red-70;
  colorA11yRed80: colors.$color-a11y-red-80;
  colorA11yRed90: colors.$color-a11y-red-90;
  colorA11yRed99: colors.$color-a11y-red-99;

  borderColorDefault: colors.$color-neutral-60; // border.default -> {neutral.60}
  borderColorLight: colors.$color-white-100; // border.light -> {white.100}
  borderColorFocus: colors.$color-black-100; // border.focus -> {black.100}
  borderColorBrandPrimary: colors.$color-red-50; // border.brand-primary -> {red.50}
  borderColorBrandSecondary: colors.$color-red-30; // border.brand-secondary -> {red.30}
  borderColorPrimaryDefault: colors.$color-neutral-60; // border.primary-default -> {neutral.60}
  borderColorPrimaryHover: colors.$color-neutral-50; // border.primary-hover -> {neutral.50}
  borderColorPrimaryActive: colors.$color-neutral-20; // border.primary-active -> {neutral.20}
  borderColorSubtle: colors.$color-neutral-80; // border.subtle -> {neutral.80}
  borderColorAdvice: colors.$color-advice-30; // border.advice -> {advice.30}
  borderColorWarning: colors.$color-warning-20; // border.warning -> {warning.20}
  borderColorError: colors.$color-error-30; // border.error -> {error.30}
  borderColorSuccess: colors.$color-success-20; // border.success -> {success.20}
  borderColorInformation: colors.$color-neutral-10; // border.information -> {neutral.10}
  borderColorPrimaryInvertedDefault: colors.$color-white-100; // {white.100}
  borderColorPrimaryInvertedHover: colors.$color-neutral-99; // {neutral.99}
  borderColorPrimaryInvertedActive: colors.$color-neutral-90; // {neutral.90}

  textColorDefault: colors.$color-neutral-10; // text.default -> {neutral.10}
  textColorSubtle: colors.$color-neutral-50; // text.subtle -> {neutral.50}
  textColorLight: colors.$color-white-100; // text.light -> {white.100}
  textColorBrandPrimary: colors.$color-red-50; // text.brand-primary -> {red.50}
  textColorBrandSecondary: colors.$color-red-30; // text.brand-secondary -> {red.30}
  textColorPrimaryDefault: colors.$color-red-30; // text.primary-default -> {red.30}
  textColorPrimaryHover: colors.$color-red-20; // text.primary-hover -> {red.20}
  textColorPrimaryActive: colors.$color-red-10; // text.primary-active -> {red.10}
  textColorAdvice: colors.$color-advice-30; // text.advice -> {advice.30}
  textColorWarning: colors.$color-warning-20; // text.warning -> {warning.20}
  textColorError: colors.$color-error-30; // text.error -> {error.30}
  textColorSuccess: colors.$color-success-20; // text.success -> {success.20}
  textColorInformation: colors.$color-neutral-10; // text.information -> {neutral.10}
  textColorPrimaryInvertedDefault: colors.$color-white-100; // text.primary-inverted-default -> {white.100}
  textColorPrimaryInvertedHover: colors.$color-neutral-99; // text.primary-inverted-hover -> {neutral.99}
  textColorPrimaryInvertedActive: colors.$color-neutral-90; // text.primary-inverted-active -> {neutral.90}

  backgroundColorDefault: colors.$color-neutral-99; // background.default -> {neutral.99}
  backgroundColorLight: colors.$color-white-100; // background.light -> {white.100}

  backgroundColorBrandPrimary: colors.$color-red-50; // background.brand-primary -> {red.50}
  backgroundColorBrandSecondary: colors.$color-red-30; // background.brand-secondary -> {red.30}

  backgroundColorPrimaryDefault: colors.$color-red-50; // background.primary-default -> {red.50}
  backgroundColorPrimaryHover: colors.$color-red-40; // background.primary-hover -> {red.40}
  backgroundColorPrimaryActive: colors.$color-red-30; // background.primary-active -> {red.30}

  backgroundColorSecondaryDefault: colors.$color-white-100; // background.secondary-default -> {white.100}
  backgroundColorSecondaryHover: colors.$color-neutral-99; // background.secondary-hover -> {neutral.99}
  backgroundColorSecondaryActive: colors.$color-neutral-90; // background.secondary-active -> {neutral.90}

  backgroundColorTertiaryDefault: colors.$color-white-100; // background.tertiary-default -> {white.100}
  backgroundColorTertiaryHover: colors.$color-neutral-99; // background.tertiary-hover -> {neutral.99}
  backgroundColorTertiaryActive: colors.$color-neutral-90; // background.tertiary-active -> {neutral.90}

  backgroundColorPrimaryInvertedDefault: colors.$color-red-30; // background.primary-inverted-default -> {red.30}
  backgroundColorPrimaryInvertedHover: colors.$color-neutral-10; // background.primary-inverted-hover -> {neutral.10}
  backgroundColorPrimaryInvertedActive: colors.$color-black-100; // background.primary-inverted-active -> {black.100}

  backgroundColorSecondaryInvertedDefault: colors.$color-red-50; // background.secondary-inverted-default -> {red.50}
  backgroundColorSecondaryInvertedHover: colors.$color-red-40; // background.secondary-inverted-hover -> {red.40}
  backgroundColorSecondaryInvertedActive: colors.$color-red-30; // background.secondary-inverted-active -> {red.30}

  backgroundColorTertiaryInvertedDefault: colors.$color-red-50; // background.tertiary-inverted-default -> {red.50}
  backgroundColorTertiaryInvertedHover: colors.$color-red-40; // background.tertiary-inverted-hover -> {red.40}
  backgroundColorTertiaryInvertedActive: colors.$color-red-30; // background.tertiary-inverted-active -> {red.30}

  backgroundColorInformation: colors.$color-neutral-10; // background.information -> {neutral.10}
  backgroundColorInformationSubtle: colors.$color-neutral-99; // background.information-subtle -> {neutral.99}

  backgroundColorAdvice: colors.$color-advice-30; // background.advice -> {advice.30}
  backgroundColorAdviceSubtle: colors.$color-advice-99; // background.advice-subtle -> {advice.99}

  backgroundColorWarning: colors.$color-warning-20; // background.warning -> {warning.20}
  backgroundColorWarningSubtle: colors.$color-warning-99; // background.warning-subtle -> {warning.99}

  backgroundColorError: colors.$color-error-30; // background.error -> {error.30}
  backgroundColorErrorSubtle: colors.$color-error-99; // background.error-subtle -> {error.99}

  backgroundColorSuccess: colors.$color-success-20; // background.success -> {success.20}
  backgroundColorSuccessSubtle: colors.$color-success-99; // background.success-subtle -> {success.99}
}
